import { isBlockEditor } from 'utils/editor-context';
import { loadEditorAsset } from 'utils/asset-loader';

/*
 * bn/location-tabs: pills switch panels (ARIA tabs, arrow keys), every panel has its own
 * Leaflet map. A map is created the first time its panel is shown — Leaflet measures the
 * container on init, and a map built inside a hidden panel would come out 0×0.
 */

const initMap = (el, markerUrl) => {
    if (el.dataset.ready) return;
    const L = window.L;
    if (!L) return;

    const lat = parseFloat(el.dataset.latitude);
    const lng = parseFloat(el.dataset.longitude);
    const zoom = parseInt(el.dataset.zoom, 10) || 12;
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;

    const map = L.map(el, { scrollWheelZoom: false }).setView([lat, lng], zoom);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap',
    }).addTo(map);

    const icon = markerUrl
        ? L.icon({ iconUrl: markerUrl, iconSize: [27, 36], iconAnchor: [13.5, 35] })
        : undefined;
    const marker = L.marker([lat, lng], icon ? { icon } : {}).addTo(map);

    if (el.dataset.label) {
        marker
            .bindTooltip(el.dataset.label, {
                permanent: true,
                direction: 'top',
                offset: [0, -38],
                className: 'bn-location-tabs__label',
            })
            .openTooltip();
    }

    el.dataset.ready = '1';
    el.bnMap = map;
};

const showPanel = (block, panel) => {
    const map = panel.querySelector('.bn-location-tabs__map');
    if (!map) return;
    if (map.dataset.ready) {
        map.bnMap.invalidateSize();
    } else {
        initMap(map, block.dataset.marker);
    }
};

const initBlock = (block) => {
    const tabs = Array.from(block.querySelectorAll('.bn-location-tabs__tab'));
    const panels = Array.from(block.querySelectorAll('.bn-location-tabs__panel'));

    const select = (tab, focus = false) => {
        tabs.forEach((t) => {
            const on = t === tab;
            t.setAttribute('aria-selected', on ? 'true' : 'false');
            t.tabIndex = on ? 0 : -1;
        });
        panels.forEach((p) => {
            p.hidden = p.id !== tab.getAttribute('aria-controls');
            if (!p.hidden) showPanel(block, p);
        });
        if (focus) tab.focus();
    };

    tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => select(tab));
        tab.addEventListener('keydown', (e) => {
            const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
            if (e.key in keys) {
                e.preventDefault();
                select(tabs[(i + keys[e.key] + tabs.length) % tabs.length], true);
            } else if (e.key === 'Home' || e.key === 'End') {
                e.preventDefault();
                select(tabs[e.key === 'Home' ? 0 : tabs.length - 1], true);
            }
        });
    });

    const visible = panels.find((p) => !p.hidden);
    if (visible) showPanel(block, visible);
};

const locationTabsInit = (block) => {
    const blocks = block
        ? Array.from(block[0].querySelectorAll('.bn-location-tabs'))
        : Array.from(document.querySelectorAll('.bn-location-tabs'));
    blocks.forEach(initBlock);
};

document.addEventListener('DOMContentLoaded', function () {
    if (!isBlockEditor()) {
        locationTabsInit();
    } else if (window.acf) {
        window.acf.addAction('render_block_preview/type=bn/location-tabs', (block) =>
            loadEditorAsset('leaflet').then(() => locationTabsInit(block))
        );
    }
});
